import { mockChannel } from "../utils/channel/mockChannel";
import { UserToken } from "../../types/token/token";
import { SearchQueryRequest } from "../../types/searchQuery/searchQuery";
import {  Document } from "../../models";
import { Tag } from "../../models/Tag";


interface SearchResult {
    Value: {
        Source: Document
    }
}

interface SearchResponse {
    data: SearchResult[]
    count: number
}

type SearchItemResponse = Document | null

const initSearchResponse: SearchResponse = {
    data: [],
    count: 0
}

interface SearchOptions {
    abortController?: AbortController | null
    returnFullData?: boolean
}

export default class SearchService {
    #token: UserToken = null
    #url: string = ''
    constructor(token: UserToken) {
        this.#token = token;
    }

    getHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token
        };
    }

    postHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token,
            'Content-Type': 'application/json'
        };
    }

    setToken = (token: UserToken, url?: string) => {
        this.#token = token;
        this.#url = url ?? '';
    }

    getToken() {
        return this.#token;
    }

    async search(searchQuery: SearchQueryRequest, options: SearchOptions): Promise<SearchResponse> {
        try {
            const address = `${this.#url}/search?returnFullData=${options.returnFullData ?? false}`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(searchQuery),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return initSearchResponse
            const result = await res.json()
            
            return result
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return initSearchResponse
            }

            return initSearchResponse
        }
    }

    async searchItem(itemId: string, options: SearchOptions): Promise<SearchItemResponse> {
        try {
            const searchQuery: SearchQueryRequest = {
                query: '',
                itemsIds: [itemId],
                page: 0,
                size: 1,
                AggType: 'FLAT',
            }
            const address = `${this.#url}/search/SearchByIds?returnFullData=${options.returnFullData ?? false}`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(searchQuery),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return null
            const result: SearchResponse = await res.json()
            if(!result || !result.data?.length || result.data[0].Value.Source.docId !== itemId) return null
            return result.data[0].Value.Source
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return null
            }
          
            return null
        }
    }
    async searchItems(itemsIds: string[], options: SearchOptions): Promise<{val: Document[], isAborted: boolean}> {
        try {
            const searchQuery: SearchQueryRequest = {
                query: '',
                itemsIds: itemsIds,
                page: 0,
                size: itemsIds.length,
                AggType: 'FLAT',
            }
            const address = `${this.#url}/search/SearchByIds?returnFullData=${options.returnFullData ?? false}`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(searchQuery),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return {val: [], isAborted: false}
            const result: SearchResponse = await res.json()
            if(!result || !result.data?.length) return {val: [], isAborted: false}
            const items = itemsIds
                .map(id => result.data.find(res => res.Value.Source.docId === id)?.Value?.Source)
                .filter(item => !!item)
            return {val: items as Document[], isAborted: false}
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: [], isAborted: true}
            }

            return {val: [], isAborted: false}
        }
    }
    async autosuggestChannels(tag: string, options: SearchOptions): Promise<{val: Tag[], isAborted: boolean}> {
        try {
            const address = `${this.#url}/autosuggest?query=${encodeURIComponent(tag)}`

            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res || !res.ok) return { val: [], isAborted: false }
            
            const suggested: {title: string, suggestType: string}[] = await res.json()
            const filteredSuggested = suggested.filter(suggest => suggest.suggestType === 'channel')
            const items = filteredSuggested.map(suggest => mockChannel(suggest.title))

            return { val: items, isAborted: false }
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: [], isAborted: true }
            }
            
            return { val: [], isAborted: false }
        }
    }
    async autosuggestItems(input: string, options: SearchOptions): Promise<{val: Document[], isAborted: boolean}> {
        try {
            const address = `${this.#url}/search`

            const query = {
                query: input,
                dataTypes: [],
                page: 0,
                size: 25,
            }

            const res = await fetch(address, {
                method: 'POST',
                headers: this.postHeaders(),
                body: JSON.stringify(query),
                signal: options?.abortController?.signal
            })
            
            if(!res || !res.ok) return { val: [], isAborted: false }
            
            const suggested: SearchResponse = await res.json()
            const items = suggested.data.map(suggest => suggest.Value.Source)

            return { val: items, isAborted: false }
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: [], isAborted: true }
            }
            
            return { val: [], isAborted: false }
        }
    }
}